import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AuthorizationRequest } from '@core/model';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IUserInfo } from '@core/model';
import {
  AuthenticationService,
} from '@core/service';
import { removeNonNumericCharacters } from '@shared/utility';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-auth-request-immediate-contact',
  templateUrl: './auth-request-immediate-contact.component.html',
  styleUrls: ['./auth-request-immediate-contact.component.scss'],
  standalone: false
})
export class AuthRequestImmediateContactComponent implements OnInit {
  @Input() authRequest: AuthorizationRequest;
  @Input() isExternalUser: boolean;
  @Input() requestType: string;
  @Output() immediateContactDetailsFormValid = new EventEmitter<boolean>();
  @Output() authRequestUpdated = new EventEmitter<AuthorizationRequest>();

  tenantId: string;
  userInfo: IUserInfo = null;
  immediateContactDetailsForm: FormGroup;
  readonly subsequentRequestType = 'Subsequent';

  constructor(
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private readonly destroyRef: DestroyRef,
  ) { }

  get contactName(): AbstractControl {
    return this.immediateContactDetailsForm.get('contactName');
  }

  get fax(): AbstractControl {
    return this.immediateContactDetailsForm.get('fax');
  }

  get phone(): AbstractControl {
    return this.immediateContactDetailsForm.get('phone');
  }

  get phoneExt(): AbstractControl {
    return this.immediateContactDetailsForm.get('phoneExt');
  }

  get contactFaxConfirmed(): AbstractControl {
    return this.immediateContactDetailsForm.get('contactFaxConfirmed');
  }

  ngOnInit() {
    // Build the Form
    this.buildImmediateContactDetailsForm();

    // Form Subscriptions
    this.immediateContactDetailsFormWatch();
    this.getUserInfo();


    // Initial Form Validation Emit
    this.immediateContactDetailsFormValid.emit(this.immediateContactDetailsForm.valid);
  }

  buildImmediateContactDetailsForm() {
    this.immediateContactDetailsForm = new FormGroup({
      //Immediate Contact Details
      contactName: new FormControl('', Validators.required),
      fax: new FormControl('', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]),
      phone: new FormControl('', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]),
      phoneExt: new FormControl(''),
      contactFaxConfirmed: new FormControl(null, this.isExternalUser ? Validators.requiredTrue : [])
    });

    if (this.requestType !== this.subsequentRequestType) {
      this.immediateContactDetailsForm.patchValue(this.authRequest.authorizationDTO);
    }
  }

  immediateContactDetailsFormWatch(): void {
    this.immediateContactDetailsForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.saveContactDetailsToAuthRequest();
      });
  }
  saveContactDetailsToAuthRequest() {
    const formValue = this.immediateContactDetailsForm.getRawValue();

    this.authRequest.authorizationDTO.contactName = formValue.contactName ?? '';
    this.authRequest.authorizationDTO.phone = formValue.phone ?? '';
    this.authRequest.authorizationDTO.fax = formValue.fax ?? '';
    this.authRequest.authorizationDTO.phoneExt = formValue.phoneExt ?? '';
    this.authRequest.authorizationDTO.contactFaxConfirmed=formValue.contactFaxConfirmed ?? ''
    this.immediateContactDetailsFormValid.emit(this.immediateContactDetailsForm.valid);
    this.authRequestUpdated.emit(this.authRequest);
  }
  setGlobalVariables() {
    this.tenantId = this.route.snapshot.params.tenantId;
  }
  private getUserInfo(): void {
    const currentUser = this.authenticationService.oid;
    if (currentUser) {
      this.authenticationService
        .getUserProfilesList([currentUser])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          this.userInfo = res?.[0];
          this.setDefaultValues();
        });

      this.authenticationService.getUser()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((userData) => {
          if (this.isExternalUser && userData) {
          if (this.authRequest.authorizationDTO.authorizationNumber || !this.authRequest.authorizationDTO.fax) {
                this.fax.setValue(removeNonNumericCharacters(userData.facsimileTelephoneNumber));
                this.fax.updateValueAndValidity();
            }
          }
        });
    }
  }
  setDefaultValues() {
    if (this.isExternalUser && this.userInfo) {
      //Immediate Contact Details fields
      if (!this.authRequest.authorizationDTO.contactName) {
        this.contactName.setValue(this.userInfo.firstName + '  ' + this.userInfo.lastName);
      }
      if (!this.authRequest.authorizationDTO.phone) {
        this.phone.setValue(this.userInfo.phone);
      }
      if (!this.authRequest.authorizationDTO.phoneExt) {
        this.phoneExt.setValue(this.userInfo.phoneExt);
      }
       this.contactFaxConfirmed.setValue(
      this.authRequest.authorizationDTO.contactFaxConfirmed ?? false
    );
    }
    else {
      this.contactName.clearValidators();
      this.phone.clearValidators();
      this.phoneExt.clearValidators();
      this.fax.clearValidators();
    }

    this.contactName.updateValueAndValidity();
    this.phone.updateValueAndValidity();
    this.phoneExt.updateValueAndValidity();
    this.fax.updateValueAndValidity();
    this.saveContactDetailsToAuthRequest();
  }
}
